import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import "dayjs/locale/es";
import { useTranslation } from "react-i18next";
import {
  BYDAY,
  emptyRecurrenceState,
  recurrenceStateFromRule,
  serializeRecurrence,
} from "./agendaRecurrence";

const DAY_LABELS = {
  SU: "D",
  MO: "S",
  TU: "T",
  WE: "Q",
  TH: "Q",
  FR: "S",
  SA: "S",
};

export default function AgendaRecurrenceDialog({ open, onClose, onSave, start, initialRule }) {
  const { t, i18n } = useTranslation("teamAgenda");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [state, setState] = useState(() =>
    initialRule ? recurrenceStateFromRule(initialRule, start) : emptyRecurrenceState(start)
  );

  useEffect(() => {
    if (!open) return;
    setState(initialRule ? recurrenceStateFromRule(initialRule, start) : emptyRecurrenceState(start));
  }, [open, initialRule, start]);

  const locale = i18n.language?.startsWith("pt")
    ? "pt-br"
    : i18n.language?.startsWith("es")
      ? "es"
      : "en";

  const unitOptions = useMemo(
    () => [
      { value: "DAILY", label: t("recurrence.unitDay") },
      { value: "WEEKLY", label: t("recurrence.unitWeek") },
      { value: "MONTHLY", label: t("recurrence.unitMonth") },
      { value: "YEARLY", label: t("recurrence.unitYear") },
    ],
    [t]
  );

  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 2 },
  };

  const handleDone = () => {
    const rule = serializeRecurrence({ ...state, preset: "custom" });
    onSave?.(rule);
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "background.paper",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>{t("recurrence.customTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 0.75 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ minWidth: 110 }}>
              {t("recurrence.repeatEvery")}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
              <TextField
                type="number"
                size="small"
                value={state.interval}
                onChange={(e) =>
                  setState((s) => ({
                    ...s,
                    interval: Math.max(1, Math.min(99, Number(e.target.value) || 1)),
                  }))
                }
                inputProps={{ min: 1, max: 99 }}
                sx={{ ...fieldSx, width: 88 }}
              />
              <TextField
                select
                size="small"
                value={state.freq}
                onChange={(e) => setState((s) => ({ ...s, freq: e.target.value }))}
                sx={{ ...fieldSx, flex: 1, minWidth: 120 }}
              >
                {unitOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          {state.freq === "WEEKLY" ? (
            <Box>
              <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ mb: 1.1 }}>
                {t("recurrence.repeatOn")}
              </Typography>
              <ToggleButtonGroup
                exclusive={false}
                size="small"
                value={state.byDay}
                onChange={(_, value) => {
                  if (!value?.length) return;
                  setState((s) => ({ ...s, byDay: value }));
                }}
                sx={{
                  flexWrap: "wrap",
                  gap: 0.85,
                  "& .MuiToggleButtonGroup-grouped": {
                    border: `1px solid ${theme.palette.divider} !important`,
                    borderRadius: "50% !important",
                    width: 38,
                    height: 38,
                    margin: 0,
                    color: "text.secondary",
                    bgcolor: alpha(theme.palette.background.default, isDark ? 0.35 : 0.65),
                    fontWeight: 700,
                    "&.Mui-selected": {
                      color: theme.palette.primary.contrastText,
                      bgcolor: theme.palette.primary.main,
                      borderColor: `${theme.palette.primary.main} !important`,
                      "&:hover": {
                        bgcolor: theme.palette.primary.dark,
                      },
                    },
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, isDark ? 0.16 : 0.08),
                    },
                  },
                }}
              >
                {BYDAY.map((code) => (
                  <ToggleButton key={code} value={code} aria-label={code}>
                    {DAY_LABELS[code]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          ) : null}

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              border: 1,
              borderColor: "divider",
              bgcolor: alpha(theme.palette.background.default, isDark ? 0.28 : 0.55),
            }}
          >
            <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ mb: 0.75 }}>
              {t("recurrence.ends")}
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={state.endType}
                onChange={(e) => setState((s) => ({ ...s, endType: e.target.value }))}
              >
                <FormControlLabel
                  value="never"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">{t("recurrence.never")}</Typography>}
                  sx={{ mb: 0.25 }}
                />
                <FormControlLabel
                  value="on"
                  control={<Radio size="small" />}
                  sx={{ alignItems: "center", mb: 0.5 }}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2">{t("recurrence.onDate")}</Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                        <DatePicker
                          disabled={state.endType !== "on"}
                          value={dayjs(state.until)}
                          onChange={(value) => {
                            if (!value?.isValid?.()) return;
                            setState((s) => ({
                              ...s,
                              endType: "on",
                              until: value.format("YYYY-MM-DD"),
                            }));
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: { ...fieldSx, width: { xs: 148, sm: 168 } },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Stack>
                  }
                />
                <FormControlLabel
                  value="after"
                  control={<Radio size="small" />}
                  sx={{ alignItems: "center" }}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2">{t("recurrence.after")}</Typography>
                      <TextField
                        type="number"
                        size="small"
                        disabled={state.endType !== "after"}
                        value={state.count}
                        onChange={(e) =>
                          setState((s) => ({
                            ...s,
                            endType: "after",
                            count: Math.max(1, Math.min(730, Number(e.target.value) || 1)),
                          }))
                        }
                        inputProps={{ min: 1, max: 730 }}
                        sx={{ ...fieldSx, width: 84 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {t("recurrence.occurrences")}
                      </Typography>
                    </Stack>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: alpha(theme.palette.background.default, isDark ? 0.2 : 0.45),
        }}
      >
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleDone}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999, px: 2.25 }}
        >
          {t("recurrence.done")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
